import { DateTime } from 'luxon';
import { v4 as uuid } from 'uuid';

import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { transactionsUtils } from 'app-components/flux/transactions';
import { isOverflowSplitItem, splitItemsToArrayOverflowLast } from 'app-components/flux/transactions/splitsHelpers';
import type { SplitItemProps, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import store from 'app-components/utils/redux-store';

import type { RecurrenceType } from './customFrequencyTypes';
import { FrequencyEnum } from './frequency';
import type {
  AutoAdjustAmount,
  ScheduledTransaction,
  ScheduledTransactionType,
  STExclude,
  STType,
} from './scheduledTransactionsTypes';
import {
  AutoAdjustAmountEnum,
  mkRecurrence,
  mkScheduledTransaction,
  STExcludeEnum,
  STTypeEnum,
} from './scheduledTransactionsTypes';

export const frequencyAliasTypes = {
  everyWeekSimplifi: 'Every week',
  everyTwoWeeksSimplifi: 'Every two weeks',
  twiceAMonthSimplifi: 'Twice a month',
  everyMonthSimplifi: 'Every month',
  every2MonthsSimplifi: 'Every 2 months',
  everyQuarterSimplifi: 'Every quarter',
  every6MonthsSimplifi: 'Every 6 months',
  everyYearSimplifi: 'Every year',
  custom: 'Custom',
} as const;

export type FrequencyAlias = (typeof frequencyAliasTypes)[keyof typeof frequencyAliasTypes];

export const frequencyAliases = [
  frequencyAliasTypes.everyWeekSimplifi,
  frequencyAliasTypes.everyTwoWeeksSimplifi,
  frequencyAliasTypes.twiceAMonthSimplifi,
  frequencyAliasTypes.everyMonthSimplifi,
  frequencyAliasTypes.every2MonthsSimplifi,
  frequencyAliasTypes.everyQuarterSimplifi,
  frequencyAliasTypes.every6MonthsSimplifi,
  frequencyAliasTypes.everyYearSimplifi,
];

export type ManualIncomeSeriesExclusionArgs = {
  create: boolean;
  isIncome: boolean;
  isBusiness: boolean;
  excludeIncomeByDefault: boolean;
  excludeF2SUserOverridden: boolean;
  accountExcludedFromBudgets: boolean;
};

/**
 * A manually-created series has no source bank transaction to derive history from.
 * `transaction` itself isn't a reliable signal on its own: the create payload always
 * builds one from the form values, manual or not. Its transaction provenance is the
 * reliable signal (see `transactionsUtils.isManualTransaction`) — a series with no
 * transaction at all is treated as manual too, since there's nothing to say otherwise.
 */
export const isManualSeries = (scheduledTransaction: Pick<ScheduledTransactionType, 'transaction'>): boolean =>
  !scheduledTransaction.transaction || transactionsUtils.isManualTransaction(scheduledTransaction.transaction);

/**
 * A logo assigned by merchant resolution (`merchantId` plus a `logoId`) is not
 * user-editable. Missing records and cleared (empty-string) ids stay editable.
 */
export const isSeriesLogoEditable = (
  series: Pick<ScheduledTransactionType, 'merchantId' | 'transaction'> | null | undefined,
): boolean => !(series?.merchantId && series.transaction?.logoId);

export const isScheduledSourceExcludedFromSpendingPlan = (
  scheduledTransaction: ScheduledTransaction | null | undefined,
): boolean =>
  Boolean(scheduledTransaction?.transaction?.isExcludedFromF2S) ||
  scheduledTransaction?.excludeF2S === STExcludeEnum.SRC ||
  scheduledTransaction?.excludeF2S === STExcludeEnum.BOTH;

export function shouldKeepManualIncomeSeriesIncludedInSpendingPlan({
  create,
  isIncome,
  isBusiness,
  ...manualIncomeSeriesDefaultArgs
}: ManualIncomeSeriesExclusionArgs): boolean {
  return (
    isIncome &&
    !isBusiness &&
    shouldApplyManualIncomeSeriesDefaults({
      create,
      ...manualIncomeSeriesDefaultArgs,
    })
  );
}

// A manual-review income series the user has explicitly toggled: the same create-income context as
// shouldKeepManualIncomeSeriesIncludedInSpendingPlan, but with the override set. Callers should leave
// the existing spending-plan exclusion untouched rather than re-deriving a category/account default.
export function isUserOverriddenManualIncomeSeries({
  excludeF2SUserOverridden,
  ...manualIncomeSeriesArgs
}: ManualIncomeSeriesExclusionArgs): boolean {
  return (
    excludeF2SUserOverridden &&
    shouldKeepManualIncomeSeriesIncludedInSpendingPlan({
      ...manualIncomeSeriesArgs,
      excludeF2SUserOverridden: false,
    })
  );
}

type CoaIdLookup = { has: (id: string) => boolean } | null | undefined;
type SpendingPlanSplitExclusionItem = Pick<SplitItemProps, 'coa' | 'isOverflow' | 'isExcludedFromF2S'>;

export function shouldApplyManualIncomeSeriesDefaults({
  create,
  excludeIncomeByDefault,
  excludeF2SUserOverridden,
  accountExcludedFromBudgets,
}: {
  create: boolean;
  excludeIncomeByDefault: boolean;
  excludeF2SUserOverridden: boolean;
  accountExcludedFromBudgets: boolean;
}): boolean {
  return create && excludeIncomeByDefault && !excludeF2SUserOverridden && !accountExcludedFromBudgets;
}

export const hasSpendingPlanExcludedNonOverflowSplits = (
  splitItems: ReadonlyArray<SpendingPlanSplitExclusionItem> | null | undefined,
): boolean => Boolean(splitItems?.some((item) => item.isOverflow !== true && item.isExcludedFromF2S));

export const clearOverflowSpendingPlanExclusions = <T extends SpendingPlanSplitExclusionItem>(
  splitItems: ReadonlyArray<T>,
): T[] =>
  splitItems.map((item) =>
    item.isOverflow === true && item.isExcludedFromF2S
      ? {
          ...item,
          isExcludedFromF2S: false,
        }
      : item,
  );

export const normalizeManualIncomeSeriesSplitExclusions = <T extends SpendingPlanSplitExclusionItem>({
  splitItems,
  shouldApplyManualIncomeDefaults,
  incomeCoaIds,
  businessCoaIds,
}: {
  splitItems: ReadonlyArray<T>;
  shouldApplyManualIncomeDefaults: boolean;
  incomeCoaIds: CoaIdLookup;
  businessCoaIds: CoaIdLookup;
}): T[] => {
  if (!shouldApplyManualIncomeDefaults) {
    return [...splitItems];
  }

  const splitItemsWithIncomeDefaults = splitItems.map((item) => {
    const splitCoaId = item.coa?.id;
    const splitCoaIsIncome = Boolean(splitCoaId && incomeCoaIds?.has(splitCoaId));
    const splitCoaIsBusiness = Boolean(splitCoaId && businessCoaIds?.has(splitCoaId));

    if (item.isOverflow !== true && splitCoaIsIncome && !splitCoaIsBusiness && item.isExcludedFromF2S) {
      return {
        ...item,
        isExcludedFromF2S: false,
      };
    }

    if (item.isOverflow !== true && splitCoaIsIncome && splitCoaIsBusiness && !item.isExcludedFromF2S) {
      return {
        ...item,
        isExcludedFromF2S: true,
      };
    }

    return item;
  });

  return hasSpendingPlanExcludedNonOverflowSplits(splitItemsWithIncomeDefaults)
    ? splitItemsWithIncomeDefaults
    : clearOverflowSpendingPlanExclusions(splitItemsWithIncomeDefaults);
};

export const weekDays = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;

export const recurrenceFromFrequencyAlias = (frequencyAlias: FrequencyAlias, dueOn?: string) => {
  let recurrence: RecurrenceType | undefined;

  if (dueOn && frequencyAlias) {
    const date = DateTime.fromISO(dueOn);

    switch (frequencyAlias) {
      case frequencyAliasTypes.everyWeekSimplifi:
        {
          const day = date && date.weekday && weekDays[date.weekday - 1];
          recurrence = {
            frequency: FrequencyEnum.WEEKLY,
            interval: 1,
            ...(day && {
              byDay: [day],
            }),
          };
        }
        break;
      case frequencyAliasTypes.everyTwoWeeksSimplifi:
        {
          const day = date && date.weekday && weekDays[date.weekday - 1];
          recurrence = {
            frequency: FrequencyEnum.WEEKLY,
            interval: 2,
            ...(day && {
              byDay: [day],
            }),
          };
        }
        break;
      case frequencyAliasTypes.twiceAMonthSimplifi:
        {
          const daysInMonth = 31;
          let day1 = date?.day;
          day1 = day1 > 28 ? day1 - daysInMonth - 1 : day1;
          const day2raw = date?.day + 15;
          let day2 = day2raw % daysInMonth;
          day2 = day2 && day2 <= 28 ? day2 : day2raw - daysInMonth - 1;
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 1,
            ...(day1 && day2
              ? {
                  byMonthDay: [day1, day2],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.everyMonthSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 1,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.every2MonthsSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 2,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.everyQuarterSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 3,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.every6MonthsSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 6,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.everyYearSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 12,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      default:
    }
  }

  return recurrence;
};

export const frequencyAliasFromRecurrence = (recurrence: any) => {
  let frequencyAlias: FrequencyAlias = frequencyAliasTypes.custom;

  if (recurrence && recurrence.frequency && recurrence.interval) {
    if (recurrence.frequency === FrequencyEnum.WEEKLY && recurrence.interval === 1) {
      frequencyAlias = frequencyAliasTypes.everyWeekSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.WEEKLY && recurrence.interval === 2) {
      frequencyAlias = frequencyAliasTypes.everyTwoWeeksSimplifi;
    } else if (
      recurrence.frequency === FrequencyEnum.MONTHLY &&
      recurrence.interval === 1 &&
      recurrence.byMonthDay &&
      recurrence.byMonthDay.length === 2
    ) {
      frequencyAlias = frequencyAliasTypes.twiceAMonthSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 1) {
      frequencyAlias = frequencyAliasTypes.everyMonthSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 6) {
      frequencyAlias = frequencyAliasTypes.every6MonthsSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 2) {
      frequencyAlias = frequencyAliasTypes.every2MonthsSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 3) {
      frequencyAlias = frequencyAliasTypes.everyQuarterSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 12) {
      frequencyAlias = frequencyAliasTypes.everyYearSimplifi;
    } else {
      frequencyAlias = frequencyAliasTypes.custom;
    }
  }

  return frequencyAlias;
};

export const STTypeNamesEnum = Object.freeze({
  [STTypeEnum.EXPENSE]: 'Bill', // Expense
  [STTypeEnum.INCOME]: 'Income',
  [STTypeEnum.TRANSFER]: 'Transfer',
  [STTypeEnum.BILL]: 'Bill',
  [STTypeEnum.SUBSCRIPTION]: 'Subscription',
  [STTypeEnum.CREDIT_CARD_PAYMENT]: 'Credit Card Payment',
  [STTypeEnum.UNKNOWN]: 'Unknown',
});

export const typeFromScheduledTransaction = (scheduledTransaction: ScheduledTransaction): STType => {
  const txn = scheduledTransaction.transaction;

  if (txn?.isSubscription) return STTypeEnum.SUBSCRIPTION;
  if (!transactionsUtils.isSplitTxn(txn) && txn?.isBill) return STTypeEnum.BILL;

  const primaryCoa = getSeriesPrimaryCoa(txn ?? null);
  return primaryCoa ? typeFromCOA(primaryCoa) : STTypeEnum.UNKNOWN;
};

export const typeFromCOA = (coa: ChartOfAccountProps): STType => {
  let stType: STType = STTypeEnum.UNKNOWN;

  if (coa) {
    if (coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY && categoriesSelectors.isIncomeCat(null, coa.id)) {
      stType = STTypeEnum.INCOME;
    } else if (
      coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT &&
      coa.id === chartOfAccountsTypes.BalanceAdjustmentTypeEnum.CREDIT_CARD_PAYMENT
    ) {
      stType = STTypeEnum.CREDIT_CARD_PAYMENT;
    } else if (
      coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT ||
      coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT
    ) {
      stType = STTypeEnum.TRANSFER;
    } else {
      stType = STTypeEnum.BILL; // Default to Bill if no specific type is found.  Doesn't distinguish between Bill and Subscription.
    }
  }
  return stType;
};

/**
 * Whether a series of derived `type` belongs under a Bills & Income type filter.
 * CREDIT_CARD_PAYMENT is a distinct derived type but has no filter of its own:
 * product-wise it is presented under Transfers. Every other filter matches the
 * derived type exactly; non-type filter values (all/canceled/suggested) never match.
 */
export const seriesMatchesTypeFilter = (type: STType, filter: string): boolean => {
  if (filter === STTypeEnum.TRANSFER) {
    return type === STTypeEnum.TRANSFER || type === STTypeEnum.CREDIT_CARD_PAYMENT;
  }
  return filter === type;
};

export const negatorFromType = (type: STType) => {
  let negator = -1;

  switch (type) {
    case STTypeEnum.BILL:
    case STTypeEnum.SUBSCRIPTION:
    case STTypeEnum.TRANSFER:
    case STTypeEnum.EXPENSE:
    case STTypeEnum.UNKNOWN:
      negator = -1;
      break;

    case STTypeEnum.INCOME:
      negator = +1;
      break;

    default:
      negator = -1;
  }

  return negator;
};

export type CoaIdSet = { has: (id: string) => boolean };
export type ScheduledSeriesClassificationModel = Pick<ScheduledTransactionType, 'transaction' | 'type'>;
type ScheduledTxnsByIdLookup = {
  get: (id: string) => Pick<ScheduledTransactionType, 'transaction'> | ScheduledTransaction | undefined;
};
export type MatchedSplitReminderSeriesBucket = 'bill' | 'subscription' | null;

/**
 * For Bills & Income (Recurring) experiences, split-series don't have a single "main" category.
 * When a series has splits, use the **first split line's** category (in array order, excluding
 * overflow placeholder lines) to determine whether the series is Income vs Bill.
 */
export const getSeriesPrimaryCoa = (txn?: TransactionProps | null): ChartOfAccountProps | null => {
  if (!txn) return null;

  if (transactionsUtils.isSplitTxn(txn)) {
    const ordered = splitItemsToArrayOverflowLast(txn.split?.items ?? []);
    const firstNonOverflow = ordered.find((item) => !isOverflowSplitItem(item));
    return firstNonOverflow?.coa ?? null;
  }

  return txn.coa ?? null;
};

/**
 * Returns the COA that UI should use when displaying a recurring series.
 * For split-series, this resolves to the first real split line's COA; otherwise
 * it falls back to the transaction's direct COA.
 */
export const getSeriesDisplayCoa = (txn?: TransactionProps | null): ChartOfAccountProps | null =>
  getSeriesPrimaryCoa(txn) ?? txn?.coa ?? null;

/**
 * Like `transactionsUtils.isIncomeFromTxn`, but for **series** classification in Bills & Income.
 * If the series is split, classification is based on the first split line's category.
 */
export const isIncomeSeriesFromTxn = (
  txn: TransactionProps | null | undefined,
  incomeCOAIds: CoaIdSet | null | undefined,
): boolean => {
  if (!txn) return false;

  // Subscriptions are always treated as Bills (never Income) in Bills & Income.
  if (txn.isSubscription) return false;

  // NOTE:
  // Split-series don't have a single "main" category and the server/UI may still carry `isBill` flags.
  // For split-series classification we intentionally rely on the first split line's category instead of `isBill`.
  if (!transactionsUtils.isSplitTxn(txn) && txn.isBill) return false;

  const primaryCoa = getSeriesPrimaryCoa(txn);
  if (!primaryCoa || primaryCoa.type !== chartOfAccountsTypes.CoaTypeEnum.CATEGORY) return false;
  if (!primaryCoa.id) return false;

  return incomeCOAIds?.has(primaryCoa.id) ?? false;
};

/**
 * Classifies a complete recurring-series model for Bills & Income surfaces.
 * Transaction semantics stay authoritative when they identify a subscription,
 * transfer, bill, or income category. The model type is the final fallback for
 * series whose transaction metadata cannot carry that distinction (for example,
 * an optimistic uncategorized Income update).
 */
export const isIncomeSeriesFromModel = (
  model: ScheduledSeriesClassificationModel | null | undefined,
  incomeCOAIds: CoaIdSet | null | undefined,
): boolean => {
  const txn = model?.transaction;
  if (txn?.isSubscription) return false;

  const isSplit = transactionsUtils.isSplitTxn(txn);
  if (!isSplit && txn?.isBill) return false;

  const primaryCoa = getSeriesPrimaryCoa(txn);
  if (
    primaryCoa?.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT ||
    primaryCoa?.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT
  ) {
    return false;
  }

  if (isIncomeSeriesFromTxn(txn, incomeCOAIds)) return true;
  if (primaryCoa?.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY && primaryCoa.id) return false;

  return model?.type?.toUpperCase() === STTypeEnum.INCOME;
};

export const isIncomeSeriesFromModelOrTxn = (
  model: ScheduledSeriesClassificationModel | null | undefined,
  txn: TransactionProps | null | undefined,
  incomeCOAIds: CoaIdSet | null | undefined,
): boolean => {
  if (model?.transaction) return isIncomeSeriesFromModel(model, incomeCOAIds);

  return isIncomeSeriesFromModel({ transaction: txn, type: model?.type }, incomeCOAIds);
};

/**
 * Canonical Bills & Income series-list filters, shared by the web Recurring
 * tabs and the mobile filter pills. `INCOME`/`BILL`/`SUBSCRIPTION`/`TRANSFER`
 * intentionally match `STTypeEnum` values so platform tab keys map 1:1.
 */
export type SeriesFilter = 'ALL' | 'INCOME' | 'BILL' | 'SUBSCRIPTION' | 'TRANSFER' | 'CANCELED' | 'SUGGESTED';

/**
 * Single source of truth for which series a Bills & Income list filter shows:
 * `SUGGESTED` is the only filter that shows unverified series, `CANCELED` the
 * only one that shows completed series, and Income vs Bill classification is
 * category-aware (`isIncomeSeriesFromModel`) rather than raw series type.
 */
export const seriesMatchesFilter = (
  scheduledTransaction: ScheduledTransaction,
  filter: SeriesFilter,
  incomeCOAIds: CoaIdSet | null | undefined,
): boolean => {
  if (filter === 'SUGGESTED') return !scheduledTransaction.isUserVerified;
  if (!scheduledTransaction.isUserVerified) return false;
  if (filter === 'CANCELED') return Boolean(scheduledTransaction.isCompleted);
  if (scheduledTransaction.isCompleted) return false;
  if (filter === 'ALL') return true;
  if (filter === 'INCOME') return isIncomeSeriesFromModel(scheduledTransaction, incomeCOAIds);

  const type = typeFromScheduledTransaction(scheduledTransaction);
  if (filter === 'BILL') {
    return type === STTypeEnum.BILL && !isIncomeSeriesFromModel(scheduledTransaction, incomeCOAIds);
  }
  return seriesMatchesTypeFilter(type, filter);
};

export const getScheduledModelTransaction = (
  txn: Pick<TransactionProps, 'stModelId'> | null | undefined,
  scheduledTxnsById: ScheduledTxnsByIdLookup | null | undefined,
): TransactionProps | null => {
  const stModelId = txn?.stModelId;
  if (!stModelId) {
    return null;
  }

  return scheduledTxnsById?.get(String(stModelId))?.transaction ?? null;
};

export const getMatchedSplitReminderSeriesBucket = ({
  txn,
  scheduledTxnsById,
  incomeCOAIds,
}: {
  txn: TransactionProps | null | undefined;
  scheduledTxnsById: ScheduledTxnsByIdLookup | null | undefined;
  incomeCOAIds: CoaIdSet | null | undefined;
}): MatchedSplitReminderSeriesBucket => {
  if (txn?.source !== 'SCHEDULED_TRANSACTION_PENDING') {
    return null;
  }
  if (!transactionsUtils.isSplitTxn(txn)) {
    return null;
  }
  if (!transactionsUtils.isTxnMatchedOrLinked(txn)) {
    return null;
  }

  const seriesTxn = getScheduledModelTransaction(txn, scheduledTxnsById) ?? txn;
  if (isIncomeSeriesFromTxn(seriesTxn, incomeCOAIds)) {
    return null;
  }

  return seriesTxn.isSubscription || txn.isSubscription ? 'subscription' : 'bill';
};

export const getDisplayName = (txn: TransactionProps) => {
  const investmentAction =
    txn && txn.investmentTxnType && transactionsUtils?.InvestmentTxnActionsByType[txn.investmentTxnType];
  const investmentActionFormatted = investmentAction ? `${investmentAction} ` : '';
  const displayName = txn?.type === 'INVESTMENT' ? `${investmentActionFormatted}${txn?.symbol}` : txn?.payee;

  return displayName;
};

export const getDisplayNameFromAdjustAmount = (amountSetting?: AutoAdjustAmount) => {
  if (!amountSetting) return '';
  if (amountSetting === AutoAdjustAmountEnum.statement) {
    return 'Statement Balance';
  }
  if (amountSetting === AutoAdjustAmountEnum.minimum) {
    return 'Minimum Balance';
  }
  if (amountSetting === AutoAdjustAmountEnum.total) {
    return 'Total Balance';
  }
  if (amountSetting === AutoAdjustAmountEnum.average) {
    return 'Average Balance';
  }
  return '';
};

const getMLKDateISO = (year: number): string => {
  const januaryFirst = DateTime.local(year, 1, 1);
  const firstMondayOffset = (8 - januaryFirst.weekday) % 7;
  const thirdMonday = januaryFirst.plus({ days: firstMondayOffset + 14 });
  return thirdMonday.toISODate() || '';
};

const weekDaysOffset = [0, 0, 0, 0, 0, 2, 1];
export const taxDueDates = (
  year: number,
): {
  q0Due: string;
  q1Start: string;
  q1End: string;
  q1Due: string;
  q2Start: string;
  q2End: string;
  q2Due: string;
  q3Start: string;
  q3End: string;
  q3Due: string;
  q4Start: string;
  q4End: string;
  q4Due: string;
} => {
  const q0Due =
    DateTime.local(year, 1, 15)
      .plus({ days: weekDaysOffset[DateTime.local(year, 1, 15).weekday - 1] })
      .toISODate() || '';
  const mlkDate0 = getMLKDateISO(year);
  const q1Due =
    DateTime.local(year, 4, 15)
      .plus({ days: weekDaysOffset[DateTime.local(year, 4, 15).weekday - 1] })
      .toISODate() || '';
  const q2Due =
    DateTime.local(year, 6, 15)
      .plus({ days: weekDaysOffset[DateTime.local(year, 6, 15).weekday - 1] })
      .toISODate() || '';
  const q3Due =
    DateTime.local(year, 9, 15)
      .plus({ days: weekDaysOffset[DateTime.local(year, 9, 15).weekday - 1] })
      .toISODate() || '';
  const q4Due =
    DateTime.local(year + 1, 1, 15)
      .plus({
        days: weekDaysOffset[DateTime.local(year + 1, 1, 15).weekday - 1],
      })
      .toISODate() || '';
  const mlkDate4 = getMLKDateISO(year + 1);

  return {
    q0Due: q0Due === mlkDate0 ? DateTime.fromISO(q0Due).plus({ days: 1 }).toISODate() || '' : q0Due,
    q1Start: DateTime.local(year, 1, 1).toISODate() || '',
    q1End: DateTime.local(year, 3, 31).toISODate() || '',
    q1Due: q1Due,
    q2Start: DateTime.local(year, 4, 1).toISODate() || '',
    q2End: DateTime.local(year, 5, 30).toISODate() || '',
    q2Due: q2Due,
    q3Start: DateTime.local(year, 6, 1).toISODate() || '',
    q3End: DateTime.local(year, 8, 31).toISODate() || '',
    q3Due: q3Due,
    q4Start: DateTime.local(year, 9, 1).toISODate() || '',
    q4End: DateTime.local(year, 12, 31).toISODate() || '',
    q4Due: q4Due === mlkDate4 ? DateTime.fromISO(q4Due).plus({ days: 1 }).toISODate() || '' : q4Due,
  };
};

export const mkEstimatedTaxesModel = (year: number, props: ScheduledTransactionType): ScheduledTransaction => {
  const dueDates = taxDueDates(year);
  const federalEstimatedTaxPaymentCategory = store
    ? categoriesSelectors.getFederalEstimatedTaxPaymentCategory(store.getState())
    : undefined;
  return mkScheduledTransaction({
    clientId: uuid().toUpperCase(),
    dueOn: dueDates.q1Due,
    matchAnyAmount: true,
    isUserVerified: true,
    recurrence: mkRecurrence({
      interval: 1,
      frequency: FrequencyEnum.YEARLY,
      endOn: dueDates.q4Due,
      byDates: [
        DateTime.fromISO(dueDates.q1Due).toFormat('M/d'),
        DateTime.fromISO(dueDates.q2Due).toFormat('M/d'),
        DateTime.fromISO(dueDates.q3Due).toFormat('M/d'),
        DateTime.fromISO(dueDates.q4Due).toFormat('M/d'),
      ],
      alias: 'MULTIPLE_FIXED_DATES',
    }),
    ...props,
    transaction: {
      payee: `United States Treasury (${year})`,
      memo: `Tax Year ${year}`,
      coa: {
        type: federalEstimatedTaxPaymentCategory?.id ? 'CATEGORY' : 'UNCATEGORIZED',
        id: federalEstimatedTaxPaymentCategory?.id || '0',
      },
      state: 'PENDING',
      matchState: 'NOT_MATCHED',
      source: 'USER_ENTERED',
      type: 'CASH_FLOW',
      amount: 0,
      ...props.transaction,
      postedOn: dueDates.q1Due,
    },
  });
};

export type SeriesExclusionSourceValues = {
  coa?: { type?: string | null } | null;
  isExcludedFromF2S?: boolean | null;
  excludeF2SDest?: boolean | null;
  isExcludedFromReports?: boolean | null;
  excludeReportsDest?: boolean | null;
};

/**
 * Maps the series editor's source/destination exclusion toggles onto the
 * scheduled-transaction SRC/DEST/BOTH exclusion payload. Transfer targets
 * (ACCOUNT coa) can exclude either leg; everything else excludes only the
 * source, and destination toggles are ignored.
 */
export const computeSeriesExclusions = (
  values: SeriesExclusionSourceValues,
): { excludeF2S: STExclude; excludeReports: STExclude } => {
  const mapLeg = (source: boolean, dest: boolean): STExclude => {
    if (source && dest) return STExcludeEnum.BOTH;
    if (dest) return STExcludeEnum.DEST;
    if (source) return STExcludeEnum.SRC;
    return STExcludeEnum.NONE;
  };
  const isTransferTarget = values?.coa?.type === 'ACCOUNT';
  return {
    excludeF2S: mapLeg(Boolean(values?.isExcludedFromF2S), isTransferTarget && Boolean(values?.excludeF2SDest)),
    excludeReports: mapLeg(
      Boolean(values?.isExcludedFromReports),
      isTransferTarget && Boolean(values?.excludeReportsDest),
    ),
  };
};
